import * as Sentry from '@sentry/browser';
import config from 'config';

import { ProductType } from '~/techstyle-shared/react-products';

import { getBrandValue } from './brandNameHelper';
import generateSkuWithoutSize from './generateSkuWithoutSize';

const cdnUrl = config.get('public.cdn');
const defaultImageSize = config.get('public.brand.imageSizes')[
  getBrandValue({ savage: 8, default: 1 })
];

/**
 * Generates an image service URL for product images with specified dimensions and model parameters.
 * It can use either a provided imageServiceUrl or construct one from product SKU information.
 *
 * @param {Object} options - Configuration options for generating the image URL
 * @param {string} [options.model='d'] - Model parameter for the image service (e.g., 'd' for default model)
 * @param {number|string} [options.angle=2] - Image angle/version. Strings are converted to 2, gift certificates use 1
 * @param {number} [options.height] - Image height in pixels, uses brand-specific defaults
 * @param {number} [options.width] - Image width in pixels, uses brand-specific defaults
 * @param {string} [options.imageServiceUrl] - Direct image service URL to use instead of constructing from product
 * @param {Object} [options.product] - Product object containing SKU, itemNumber, alias, or productTypeId
 * @param {string} [options.product.sku] - Product SKU (preferred identifier)
 * @param {string} [options.product.itemNumber] - Product item number (fallback identifier)
 * @param {string} [options.product.item_number] - Alternative item number property name
 * @param {string} [options.product.alias] - Product alias (fallback identifier)
 * @param {number} [options.product.productTypeId] - Product type ID for special handling (membership, gift certificates)
 * @returns {string|null} The generated image service URL with query parameters, or null if insufficient data provided
 */
function generateImageServiceImageUrl({
  model = 'd',
  angle = 2,
  height = defaultImageSize.height,
  width = defaultImageSize.width,
  imageServiceUrl,
  product,
}) {
  let urlRoot = '';
  const itemNumber = product?.item_number || product?.itemNumber;
  if (
    !imageServiceUrl &&
    !product?.sku &&
    !itemNumber &&
    !product?.alias &&
    !(product?.productTypeId === ProductType.MEMBERSHIP_FEE_PLACEHOLDER)
  ) {
    Sentry.captureMessage(
      `Error: 'generateImageServiceImageUrl' No imageServiceUrl or product with required properties provided.`,
      'warning'
    );
    return null;
  }

  if (typeof angle === 'string') {
    angle = 2; // it doesn't matter if it's 'laydown' or not, we will default to angle 2 if its an invalid string
  }

  if (product?.productTypeId === ProductType.MEMBERSHIP_FEE_PLACEHOLDER) {
    return `${cdnUrl}${config.get(
      'public.membershipProductImage.thumbnailURL'
    )}`;
  }

  if (product?.productTypeId === ProductType.GIFT_CERTIFICATE) {
    angle = 1; // Always use angle 1 for gift cards
  }

  if (imageServiceUrl) {
    urlRoot = imageServiceUrl;
  } else {
    const sku = product?.sku || generateSkuWithoutSize(product);
    // Strip off size if it does exist
    const cleanedSku = sku?.replace(/(-\d+)-\d+$/, '$1');
    urlRoot = `${cdnUrl}/assets/images/skus/${cleanedSku}`;
  }

  // Strip leading protocol or double slashes so we can safely prepend '//'
  const cleanUrl = urlRoot.replace(/^(https?:)?\/\//, '');

  return `//${cleanUrl}?model=${model}&version=${angle}&imgSize=${width}x${height}`;
}

export default generateImageServiceImageUrl;
